import { useNavigate } from 'react-router-dom';
import {
  pickWorkspace,
  readLastWorkspace,
  readLastWorkspaceAt,
  relativeTimeLowercase,
  WORKSPACE_LABEL,
  WORKSPACE_PATH,
  type Workspace,
} from './workspace';

export function Selector() {
  const nav = useNavigate();
  const lastWs = readLastWorkspace();
  const lastAt = relativeTimeLowercase(readLastWorkspaceAt());

  function open(ws: Workspace) {
    pickWorkspace(ws);
    nav(WORKSPACE_PATH[ws], { replace: true });
  }

  return (
    <div className="min-h-screen w-screen flex flex-col items-center justify-center bg-bg text-text font-body relative">
      <header className="text-center mb-12">
        <div className="font-display text-[44px] leading-none text-accent-bright tracking-tight">Timberline</div>
        <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.3em] text-text-faint">
            ─── pick your workspace ───
        </div>
      </header>

      <div className="flex gap-6">
        <Tile
          mark={<MineralMark />}
          name="Mineral Intelligence"
          description="Mineral rights data and lead intelligence"
          onOpen={() => open('mi')}
        />
        <Tile
          mark={<DealDeskMark />}
          name="Deal Desk"
          description="Titled deals: chain of conveyance, worksheet, pricing"
          onOpen={() => open('deal-desk')}
        />
      </div>

      {lastWs && lastAt && (
        <div className="mt-12 font-mono text-[11px] lowercase tracking-wide text-text-faint">
          last opened {WORKSPACE_LABEL[lastWs]} · {lastAt}
        </div>
      )}
    </div>
  );
}

interface TileProps {
  mark: React.ReactNode;
  name: string;
  description: string;
  onOpen: () => void;
}

function Tile({ mark, name, description, onOpen }: TileProps) {
  return (
    <button
      onClick={onOpen}
      className="
        group w-[320px] h-[360px]
        bg-bg-card-soft border border-border rounded
        flex flex-col items-center justify-between
        px-8 py-10
        hover:bg-bg-card hover:border-accent/60
        transition-colors text-left
      "
    >
      <div className="flex-1 flex items-center justify-center w-full">
        <div className="text-accent group-hover:text-accent-bright transition-colors">{mark}</div>
      </div>
      <div className="text-center mt-6">
        <div className="font-display text-[22px] leading-tight">{name}</div>
        <div className="mt-2 text-[12px] text-text-dim leading-snug px-2">{description}</div>
      </div>
      <div className="mt-7">
        <span className="
          inline-block px-5 py-2
          bg-accent text-bg text-[13px] font-medium rounded
          group-hover:bg-accent-bright
        ">Open →</span>
      </div>
    </button>
  );
}

// Deal Desk gets a vertical chain-of-title mark: three linked nodes,
// same stroke language as the MI diamond. Inline SVG, no new asset.
function DealDeskMark() {
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <circle cx="36" cy="12" r="7" stroke="currentColor" strokeWidth="2" strokeDasharray="3 3" />
      <path d="M36 19 L36 29" stroke="currentColor" strokeWidth="2" />
      <circle cx="36" cy="36" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="M36 43 L36 53" stroke="currentColor" strokeWidth="2" />
      <circle cx="36" cy="60" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="M43 36 L57 36 M57 32 L57 40" stroke="currentColor" strokeWidth="1.2" opacity="0.55" />
      <path d="M29 60 L15 60 M15 56 L15 64" stroke="currentColor" strokeWidth="1.2" opacity="0.55" />
    </svg>
  );
}

// MI gets a brass diamond — terse, distinct from the Workbench tree mark.
// Inline SVG so we don't ship a new asset for one glyph.
function MineralMark() {
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path
        d="M36 6 L62 30 L36 66 L10 30 Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M10 30 L62 30 M22 30 L36 6 L50 30 L36 66 Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
        opacity="0.55"
      />
    </svg>
  );
}
